import { useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { undoLast, redoLast } from '../lib/history';
import { reportOutcome } from '../lib/historyReport';

/**
 * App-level Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y (#278).
 *
 * Inside a text field or the editor the keys belong to that control (TipTap's
 * keystroke undo stays fine-grained); anywhere else they undo or redo the
 * user's latest action - from any device, typing bursts included. Every undo
 * says what it undid, since a typing step is not in the history list.
 */

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
    || target.isContentEditable || !!target.closest('.tiptap, [contenteditable="true"]');
}

export function HistoryShortcuts() {
  const toast = useToast();

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = (key === 'z' && e.shiftKey) || (key === 'y' && !e.shiftKey);
      if (!isUndo && !isRedo) return;
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      void (isUndo ? undoLast() : redoLast()).then((r) => reportOutcome(toast, r, isUndo ? 'undo' : 'redo'));
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [toast]);

  return null;
}
